/**
 * Work Order Flow Hook
 *
 * Fetches the tenant's work order flow configuration from the API.
 * Provides statuses, transitions, kanban columns, colors, and icons.
 * Cached with a long staleTime since flow config rarely changes.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { statusColors } from '@/styles/theme';
import { queryKeys } from './queryKeys';

// Types matching the API response (ResolvedFlowConfig from backend)
interface StatusDef {
  key: string;
  color: string;
  icon: string;
  defaultSort: number;
  isTerminal: boolean;
}

interface FlowConfigResponse {
  statuses: StatusDef[];
  transitions: Record<string, string[]>;
  kanbanColumns: string[];
  conditionalStatuses: string[];
  autoDerivation: {
    enabled: boolean;
    manualStatuses: string[];
  };
}

export interface WorkOrderFlowData {
  /** Ordered list of enabled statuses with metadata */
  statuses: StatusDef[];
  /** Allowed transitions: status → next statuses */
  transitions: Record<string, string[]>;
  /** Statuses shown as kanban board columns */
  kanbanColumns: string[];
  /** Statuses hidden unless visited */
  conditionalStatuses: Set<string>;
  /** Map of status key → color hex */
  statusColors: Record<string, string>;
  /** Map of status key → icon name */
  statusIcons: Record<string, string>;
  /** Get allowed next statuses from a given status */
  getNextStatuses: (currentStatus: string) => string[];
  /** Check if transition is allowed */
  isValidTransition: (from: string, to: string) => boolean;
  /** Whether data is still loading */
  isLoading: boolean;
}

// Static fallback matching the default config (used while loading).
// Single source of truth: the brand-aligned `statusColors` in styles/theme.ts.
const FALLBACK_COLORS: Record<string, string> = statusColors;

export function useWorkOrderFlow(): WorkOrderFlowData {
  const { data, isLoading } = useQuery({
    queryKey: queryKeys.workOrders.flow(),
    queryFn: () => api.get<FlowConfigResponse>('/work-orders/flow'),
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
  });

  const statusColors = useMemo(() => {
    if (!data) return FALLBACK_COLORS;
    const colors: Record<string, string> = { ...FALLBACK_COLORS };
    for (const status of data.statuses) {
      colors[status.key] = status.color;
    }
    return colors;
  }, [data]);

  const statusIcons = useMemo(() => {
    if (!data) return {};
    const icons: Record<string, string> = {};
    for (const status of data.statuses) {
      icons[status.key] = status.icon;
    }
    return icons;
  }, [data]);

  const conditionalStatuses = useMemo(() => {
    if (!data) return new Set(['paused']);
    return new Set(data.conditionalStatuses);
  }, [data]);

  const transitions = useMemo(
    () => data?.transitions ?? {},
    [data?.transitions],
  );

  const getNextStatuses = useMemo(() => {
    return (currentStatus: string): string[] => {
      return transitions[currentStatus] ?? [];
    };
  }, [transitions]);

  const isValidTransition = useMemo(() => {
    return (from: string, to: string): boolean => {
      const allowed = transitions[from];
      return allowed ? allowed.includes(to) : false;
    };
  }, [transitions]);

  return {
    statuses: data?.statuses ?? [],
    transitions,
    kanbanColumns: data?.kanbanColumns ?? [],
    conditionalStatuses,
    statusColors,
    statusIcons,
    getNextStatuses,
    isValidTransition,
    isLoading,
  };
}
